import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { COUNTRIES, countryFor, type Country } from '@matix/utils';
import { useT } from '../../lib/i18n';

/**
 * A mobile number, entered so that it can only mean one thing.
 *
 * The portal signs a customer in by messaging their phone, which makes the
 * number their identity — and `01211680705` typed by a returning customer has
 * to reach the same account as the `+201211680705` sitting in the database.
 * A dial code the customer picks (opened on the salon's own country) is what
 * removes the ambiguity at the moment of typing, rather than leaving the server
 * to guess afterwards.
 *
 * The number itself is always `dir="ltr"` even in Arabic: a phone number is
 * read left-to-right in every locale, and letting it flip puts the country code
 * on the wrong end.
 *
 * THE COUNTRY LIST IS A PORTAL, and that is not decoration. This field lives
 * inside the sign-in sheet, whose panel is `overflow-y-auto` and capped at
 * 88dvh so a tall registration form stays reachable. An absolutely positioned
 * list inside that panel is clipped by it — two hundred countries hanging out
 * of a rounded white box — and its height makes the panel itself scroll, so the
 * sheet grows a scrollbar the moment the picker opens. Rendered to the body and
 * positioned `fixed` against the field, the list is bounded by the viewport
 * instead, and the sheet behind it does not move at all.
 */
export function PhoneField({
  iso,
  onIso,
  value,
  onValue,
  inputRef,
  autoFocus,
}: {
  iso: string;
  onIso: (iso: string) => void;
  value: string;
  onValue: (value: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  autoFocus?: boolean;
}): JSX.Element {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [box, setBox] = useState<PopoverBox | null>(null);

  const selected = countryFor(iso) ?? COUNTRIES[0]!;

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...COUNTRIES].sort((a, b) =>
      labelOf(a, lang).localeCompare(labelOf(b, lang), lang === 'ar' ? 'ar' : 'en'),
    );
    if (!q) return sorted;
    return sorted.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.nameAr.includes(query.trim()) ||
        c.dial.startsWith(q.replace(/^\+/, '')) ||
        c.iso.toLowerCase() === q,
    );
  }, [query, lang]);

  /**
   * Where the list should sit, measured against the live viewport.
   *
   * Below the field when there is room, above it when there is not — the sheet
   * is bottom-anchored on a phone, so "below" is frequently three pixels of
   * screen. The height is whatever that side actually offers, capped at the
   * design's 18rem, so the list is never taller than the space it is in.
   */
  const place = useCallback((): void => {
    const row = rowRef.current;
    if (!row) return;
    const r = row.getBoundingClientRect();
    const gap = 8;
    const margin = 12;
    const below = window.innerHeight - r.bottom - gap - margin;
    const above = r.top - gap - margin;
    const flip = below < 200 && above > below;
    const maxHeight = Math.max(140, Math.min(288, flip ? above : below));
    setBox({
      left: r.left,
      width: r.width,
      top: flip ? r.top - gap - maxHeight : r.bottom + gap,
      maxHeight,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place]);

  // The sheet behind this scrolls, so a position measured once goes stale.
  // Capture-phase catches the panel's own scroll, not just the window's.
  useEffect(() => {
    if (!open) return;
    const onMove = (): void => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  // Click-outside and Escape both close the list. Without this it stays open
  // over the submit button on a phone, which looks like the sheet has frozen.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      // The list is a portal, so it is NOT inside boxRef. Miss it here and
      // mousedown unmounts the list before the option's click can fire, which
      // reads as a picker that refuses to pick.
      if (boxRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    setTimeout(() => searchRef.current?.focus(), 40);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <div ref={boxRef} className="relative">
      <span className="bk-label">{t('phone_label')}</span>
      <div ref={rowRef} className="flex gap-2" dir="ltr">
        <button
          type="button"
          onClick={() => {
            setQuery('');
            setOpen((v) => !v);
          }}
          aria-label={t('country_label')}
          aria-expanded={open}
          className="bk-input flex w-[7.5rem] shrink-0 items-center justify-between gap-1 px-3"
        >
          <span className="flex items-center gap-1.5 truncate">
            <span aria-hidden className="text-lg leading-none">
              {selected.flag}
            </span>
            <span className="text-sm text-zinc-700">+{selected.dial}</span>
          </span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <input
          ref={inputRef}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          className="bk-input flex-1"
          dir="ltr"
          value={value}
          onChange={(e) => onValue(e.target.value)}
          placeholder="5xx xxx xxx"
          required
        />
      </div>

      {open &&
        box &&
        createPortal(
          <div
            ref={listRef}
            // No dir here on purpose: the portal hangs off <body>, so it
            // inherits the direction App.tsx puts on <html> and the Arabic list
            // reads right-to-left exactly as it did in place.
            // Above the sheet's own z-50, or the backdrop paints over it.
            className="bk-shadow fixed z-[60] flex flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white p-2"
            style={{
              left: box.left,
              top: box.top,
              width: box.width,
              maxHeight: box.maxHeight,
            }}
          >
            <input
              ref={searchRef}
              type="text"
              className="bk-input mb-2 !min-h-[40px] shrink-0 text-sm"
              placeholder={t('country_search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {/* Only the list scrolls; the search box stays put, which is the
                whole point of having one for two hundred rows. */}
            <ul role="listbox" className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {list.map((c) => (
                <li key={c.iso}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={c.iso === selected.iso}
                    onClick={() => {
                      onIso(c.iso);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-start text-sm hover:bg-zinc-50 ${
                      c.iso === selected.iso ? 'bg-zinc-50 font-medium' : ''
                    }`}
                  >
                    <span aria-hidden className="text-lg leading-none">
                      {c.flag}
                    </span>
                    <span className="flex-1 truncate text-zinc-800">{labelOf(c, lang)}</span>
                    <span dir="ltr" className="text-zinc-500">
                      +{c.dial}
                    </span>
                  </button>
                </li>
              ))}
              {list.length === 0 && (
                <li className="px-3 py-6 text-center text-sm text-zinc-500">{t('country_none')}</li>
              )}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}

interface PopoverBox {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
}

function labelOf(country: Country, lang: string): string {
  return lang === 'ar' ? country.nameAr : country.name;
}
